import React from 'react'
import ReactDOM from 'react-dom/client'
import { clearLaunchFromHistory, getPreviewEntry } from './preview/entry'
import { consumeReviewEntry, type ReviewEntry } from './review/entry'
import { FlagsProvider } from './flags'
import { isAuthConfigured } from './auth/config'
import ChunkLoadError from './bootstrap/ChunkLoadError'
import { clearChunkReloadFlag, guardChunkImport } from './bootstrap/chunkRecovery'
import { initAccent } from './ui/accent'
import { configureLogging } from './log/logger'
import { installGlobalErrorCapture } from './log/globalErrors'
import './index.css'

// Put the user's accent on <html> BEFORE the first render, so a non-default
// palette never flashes amber on load. Shared by every entry below (authoring,
// preview, review, the ?demo=* harnesses) — the attribute is inert when unset.
initAccent()

// Frontend logging (src/log): every getLog(origin) call goes to the Console's
// Logs tab and the browser console. Set up before anything renders or loads,
// so the earliest lines and uncaught errors are not lost.
configureLogging()
installGlobalErrorCapture()

const root = ReactDOM.createRoot(document.getElementById('root')!)

// Shared recovery wiring for the production standalone import paths: a lazy
// chunk that 404s during a deploy race hard-reloads ONCE (sessionStorage
// guard), then falls back to a visible branded error rather than a silent
// blank. See src/bootstrap/chunkRecovery.ts.
const chunkRecovery = {
  storage: window.sessionStorage,
  reload: () => window.location.reload(),
  renderError: () => root.render(<ChunkLoadError onReload={() => window.location.reload()} />),
}
const previewOnly = import.meta.env.VITE_OROVIA_PREVIEW_ONLY === 'true'
const reviewOnly = import.meta.env.VITE_OROVIA_REVIEW_ONLY === 'true'

const demo = new URLSearchParams(window.location.search).get('demo')

// DEV-only harness for the AI processing panel (ENG-1142). Statically false in
// production builds, so the harness + mock modules tree-shake out of every
// bundle (authoring, preview-only, review-only).
const aiHarness = import.meta.env.DEV && demo === 'ai'
// DEV-only harness for the standalone auth shell (ENG-1166). Same tree-shaking
// guarantee: the mock auth client + mock flags never reach a production bundle.
const authHarness = import.meta.env.DEV && demo === 'auth'
// DEV-only harness for the standalone-AI flow (ENG-1206): the real
// AuthenticatedShell → App with a mock upload + processing api. Same tree-shaking
// guarantee — the harness + every mock it imports drop from production bundles.
const aiStandaloneHarness = import.meta.env.DEV && demo === 'ai-standalone'
// DEV-only harness for the Case Report metrics dashboard. Same tree-shaking
// guarantee: the harness + its fixture drop from every production bundle.
const caseReportHarness = import.meta.env.DEV && demo === 'report'
// DEV-only harness for the premium AI-analysis presentation (hero HUD +
// contextual inspector + camera fly-to on the real r3f viewport). Same
// tree-shaking guarantee: the harness + its fixture drop from every prod bundle.
const analysisHarness = import.meta.env.DEV && demo === 'analysis'
// DEV-only harness for the retainer TWO-TIER interactive builder. Same
// tree-shaking guarantee: the harness + its mock session client drop from every
// production bundle. `?demo=retainer-builder&live=1` drives the REAL session
// server (retainer-session compose service) instead of the mock.
const retainerBuilderHarness = import.meta.env.DEV && demo === 'retainer-builder'
// DEV-only staff jobs monitor at `/dev/jobs` (docs/dev-jobs.md). Statically
// false in production builds, so the lazy chunk + everything under src/dev/jobs
// drop from every bundle. Gated by the same Cognito session as the standalone app.
const devJobsRoute =
  import.meta.env.DEV && window.location.pathname.replace(/\/+$/, '') === '/dev/jobs'

// DEV-only: `?mode=ai` mounts the REAL standalone <App/> with every ai.* gate
// forced ON via an injected flags fetch — no backend, no auth, and (unlike the
// `?demo=ai-standalone` harness) NO fixture autoload. The gate flip and the
// geometry autoload are unrelated; this path exercises only the former.
// Statically false in production builds, so this branch + the injected fetch
// below tree-shake out of every bundle.
const aiMode =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('mode') === 'ai'

// Every ai.* flag consumed by useStandaloneAi/App, forced true for `?mode=ai`.
const AI_MODE_FLAGS: Record<string, boolean> = {
  'ai.processing': true,
  'ai.tseglab': true,
  'ai.crown_prep': true,
  // OFF in the dev harness on purpose: the merged Segment button must be
  // exercised on its FALLBACK path here, because that is the only path that
  // exists until the prep_seg worker plane is deployed. Flip to true to drive
  // the combined path against a backend that has it.
  'ai.prep_seg': false,
  'ai.bite_align': true,
  'ai.locator_fixed': true,
  'ai.scanqc': true,
  'ai.retainer': true,
  'ai.retainer.two_tier': true,
  'ai.retainer.gumline': true,
  'ai.cdm2mesh': true,
  'ai.margin_tracer': true,
}

// Injected into FlagsProvider for `?mode=ai`: a synchronous 200 with the flags
// above, so the per-principal gate resolves ON with no network/backend.
const devAiFlagsFetch: typeof fetch = () =>
  Promise.resolve(
    new Response(
      JSON.stringify({
        flags: AI_MODE_FLAGS,
        evaluatedAt: '2026-01-01T00:00:00.000Z',
        ttlSeconds: 86_400,
      }),
      { status: 200, headers: { 'content-type': 'application/json' } },
    ),
  )

if (reviewOnly) {
  renderReview(consumeReviewEntry(window.location, window.history))
} else if (previewOnly) {
  renderPreview(getPreviewEntry(window.location.search, true))
} else if (aiHarness) {
  renderAiHarness()
} else if (authHarness) {
  renderAuthHarness()
} else if (aiStandaloneHarness) {
  renderAiStandaloneHarness()
} else if (caseReportHarness) {
  renderCaseReportHarness()
} else if (analysisHarness) {
  renderAnalysisHarness()
} else if (retainerBuilderHarness) {
  renderRetainerBuilderHarness()
} else if (devJobsRoute) {
  renderDevJobs()
} else {
  const reviewEntry = window.location.pathname.startsWith('/review/')
    ? consumeReviewEntry(window.location, window.history)
    : null
  const entry = getPreviewEntry(window.location.search)
  if (reviewEntry) renderReview(reviewEntry)
  else if (entry.mode === 'preview') renderPreview(entry)
  else renderStandalone()
}

function renderAiHarness() {
  void import('./preview/AiPreviewHarness').then(({ default: AiPreviewHarness }) => {
    root.render(<AiPreviewHarness />)
  })
}

function renderAuthHarness() {
  void import('./auth/AuthHarness').then(({ default: AuthHarness }) => {
    root.render(<AuthHarness />)
  })
}

function renderAiStandaloneHarness() {
  void import('./auth/AiStandaloneHarness').then(({ default: AiStandaloneHarness }) => {
    root.render(<AiStandaloneHarness />)
  })
}

function renderCaseReportHarness() {
  void import('./preview/CaseReportHarness').then(({ default: CaseReportHarness }) => {
    root.render(<CaseReportHarness />)
  })
}

function renderAnalysisHarness() {
  void import('./preview/PremiumAnalysisHarness').then(({ default: PremiumAnalysisHarness }) => {
    root.render(<PremiumAnalysisHarness />)
  })
}

function renderRetainerBuilderHarness() {
  void import('./preview/RetainerBuilderHarness').then(({ default: RetainerBuilderHarness }) => {
    root.render(<RetainerBuilderHarness />)
  })
}

function renderDevJobs() {
  // Not StrictMode: when logged out this mounts AuthApp, whose one-time OAuth
  // exchange must never be replayed (same posture as renderStandalone).
  const DevJobsEntry = React.lazy(() => import('./dev/jobs/DevJobsEntry'))
  root.render(
    <React.Suspense fallback={null}>
      <DevJobsEntry />
    </React.Suspense>,
  )
}

function renderReview(entry: ReviewEntry) {
  void import('./review/ReviewApp').then(({ default: ReviewApp }) => {
    root.render(<ReviewApp entry={entry} historyCleared />)
  }).catch(() => {
    root.render(
      <main role="alert" style={{ padding: 32 }}>
        Unable to open the secure review. Return to ClearPath and try again.
      </main>,
    )
  })
}

function renderPreview(entry: Extract<ReturnType<typeof getPreviewEntry>, { mode: 'preview' }>) {
  // Remove the one-time capability synchronously, before even waiting for the
  // preview chunk. Preview intentionally avoids StrictMode's development
  // effect replay because launch exchange must never be repeated ambiguously.
  clearLaunchFromHistory(window.location, window.history)
  void import('./preview/PreviewApp').then(({ default: PreviewApp }) => {
    root.render(
      <FlagsProvider>
        <PreviewApp entry={entry} historyCleared />
      </FlagsProvider>,
    )
  }).catch(() => {
    root.render(
      <main role="alert" style={{ padding: 32 }}>
        Unable to open the secure preview. Return to ClearPath and try again.
      </main>,
    )
  })
}

function renderStandalone() {
  // A deployed standalone (auth env configured) requires a Cognito login before
  // the app mounts — the AuthApp shell resolves the session and gates the app
  // behind a per-principal FlagsProvider. Local dev with no auth env keeps the
  // un-gated viewer (existing behaviour, and what the ?demo=* flows exercise).
  //
  // AuthApp is intentionally NOT wrapped in StrictMode: the one-time OAuth code
  // exchange must never be replayed by StrictMode's development effect double-
  // invoke (same posture as the preview launch exchange).
  if (isAuthConfigured()) {
    // AuthenticatedShell statically imports App, so a missing App-*.js also
    // breaks the AuthApp chunk — guard this path too or login never renders.
    void guardChunkImport(
      import('./auth/AuthApp').then(({ default: AuthApp }) => {
        root.render(<AuthApp />)
        clearChunkReloadFlag(window.sessionStorage)
      }),
      chunkRecovery,
    )
    return
  }
  void guardChunkImport(
    import('./App').then(({ default: App }) => {
      root.render(
        <React.StrictMode>
          {aiMode ? (
            <FlagsProvider apiBase="dev://mode-ai" fetchImpl={devAiFlagsFetch}>
              <App />
            </FlagsProvider>
          ) : (
            <App />
          )}
        </React.StrictMode>,
      )
      clearChunkReloadFlag(window.sessionStorage)
    }),
    chunkRecovery,
  )
}
